import { UzButton } from '@utazzo/ui';
import { Download, Share, X } from 'lucide-react';
import React, { useState, useSyncExternalStore } from 'react';

import { canPromptInstall, onInstallPromptChange, promptInstall } from '../install-prompt';

import { isAppleMobile, isPhoneDevice, isStandaloneApp } from '@/lib/device';

/**
 * Post-login "install the app" banner, phones only.
 *
 * Adapts to the platform because there is no single install path:
 *   - Android / Chromium: the browser hands us a `beforeinstallprompt`, so we
 *     show a real "Install" button that fires it.
 *   - iOS: no prompt ever fires (Apple restricts install to Safari's "Add to
 *     Home Screen"), so we show the Share -> Add to Home Screen steps instead,
 *     and nudge to Safari when the user is in iOS Chrome/Firefox/Edge.
 *
 * Hidden once installed (standalone), on desktop (`lg:hidden` + phone gate), and
 * for a cool-down after the user dismisses it so it never nags.
 */
const DISMISS_KEY = 'utazzo.admin.installBannerDismissedAt';
const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // re-offer after 14 days

const recentlyDismissed = (): boolean => {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    return Number.isFinite(ts) && Date.now() - ts < COOLDOWN_MS;
  } catch {
    return false;
  }
};

const isIosNonSafari = (): boolean => {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  return isAppleMobile() && /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
};

export const MobileInstallBanner = (): React.JSX.Element | null => {
  const installable = useSyncExternalStore(onInstallPromptChange, canPromptInstall, () => false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return true;
    return recentlyDismissed();
  });

  if (dismissed || typeof window === 'undefined') return null;
  // Phones only, and never inside the already-installed app.
  if (!isPhoneDevice() || isStandaloneApp()) return null;

  const apple = isAppleMobile();
  // A non-Apple phone whose browser hasn't offered a prompt yet: stay quiet
  // rather than show a banner with no actionable path.
  if (!apple && !installable) return null;

  const dismiss = (): void => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore - dismissal is best-effort */
    }
    setDismissed(true);
  };

  const onInstall = (): void => {
    void promptInstall().then((ok) => {
      if (ok) setDismissed(true);
    });
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 border-t border-[var(--ds-border)] bg-[var(--bg)] p-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] lg:hidden"
      role="dialog"
      aria-label="Install the Utazzo app"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[var(--r-md)] bg-[var(--z-100)] text-[var(--fg)]">
          <Download className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[var(--fg)]">Install the Utazzo app</p>
          <p className="mt-0.5 text-xs text-[var(--fg-muted)]">
            {installable
              ? 'Add Utazzo to your home screen for a faster, full-screen experience.'
              : isIosNonSafari()
                ? 'Open this page in Safari, then tap Share and choose "Add to Home Screen".'
                : 'Tap the Share button, then choose "Add to Home Screen".'}
          </p>
          <div className="mt-2 flex items-center gap-2">
            {installable ? (
              <UzButton size="sm" onClick={onInstall}>
                <Download className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Install
              </UzButton>
            ) : apple ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--fg-muted)]">
                <Share className="h-3.5 w-3.5" aria-hidden />
                Share then Add to Home Screen
              </span>
            ) : null}
            <UzButton size="sm" variant="ghost" onClick={dismiss}>
              Not now
            </UzButton>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install banner"
          className="-mt-1 -mr-1 flex-shrink-0 rounded-[var(--r-md)] p-1 text-[var(--fg-faint)] hover:text-[var(--fg)]"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
};
